import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { task } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsTextDisplay,
  HdsTextBody,
  HdsButton,
  HdsTable,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import { Time, Flex, FlexGrid } from 'core/components';
import { boxPadding } from 'core/modifiers';
import Delete from 'hcp/components/scim/tokens/delete';

/**
 *
 * `ScimTokensList` displays the token to be considered for removal in a modal
 *
 * ```
 * <Scim::Tokens::List
 *  @openTokenModal={{@model.openTokenModal}}
 *  @tokens={{@model.tokens}}
 *  @organization={{@model.organization}}
 * />
 * ```
 *
 * @class ScimTokensList
 *
 */
export default class ScimTokensList extends Component {
  /**
   * @argument openTokenModal
   * @type {Function}
   */
  /**
   * The tokens attached to a given organization
   * @argument tokens
   * @type {Object}
   * @required
   */
  /**
   * The organization
   * @argument organization
   * @type {Object}
   * @required
   */

  @service api;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked tokenToDelete;
  @tracked showDeleteTokenModal = false;

  /**
   * Removes a scim token from an organization.
   */
  deleteToken = task(async () => {
    try {
      await this.api.scim.scimServiceDeleteScimToken(
        this.args.organization.id,
        this.tokenToDelete.tokenId
      );
      this.flashMessages.success(
        this.intl.t(`components.scim.tokens.delete.success.title`, {
          tokenName: this.tokenToDelete.displayName,
          htmlSafe: true,
        })
      );
      this.closeDeleteTokenModal();
      this.refreshRoute();
      return;
    } catch {
      this.flashMessages.error(
        this.intl.t(`components.scim.tokens.delete.error.title`, {
          tokenName: this.tokenToDelete.displayName,
          htmlSafe: true,
        }),
        {
          content: this.intl.t(`components.scim.tokens.delete.error.content`),
        }
      );
      this.closeDeleteTokenModal();
    }
  });

  openDeleteTokenModal = (token) => {
    this.showDeleteTokenModal = true;
    this.tokenToDelete = token;
  };

  closeDeleteTokenModal = () => {
    if (this.isDestroyed || this.isDestroying) return;
    this.showDeleteTokenModal = false;
    this.tokenToDelete = null;
  };

  refreshRoute = () => {
    let route = getOwner(this).lookup(
      `route:${this.router.currentRoute.parent.name}`
    );
    return route.refresh();
  };

  <template>
    <FlexGrid {{boxPadding "md 0 lg 0"}} as |G|>
      <G.Item @xs="12" @sm="9" @md="9" @lg="10">
        <HdsTextDisplay @size="200" @tag="h4">
          {{t "components.scim.tokens.list.title"}}
        </HdsTextDisplay>
        <HdsTextBody @size="200" @tag="p">
          {{t "components.scim.tokens.list.description"}}
        </HdsTextBody>
      </G.Item>
      {{#if (can "create scim-token")}}
        <G.Item @xs="12" @sm="3" @md="3" @lg="2">
          <HdsButton
            @icon="plus"
            @text={{t "components.scim.tokens.list.action"}}
            {{on "click" @openTokenModal}}
            data-test-create-scim-token
          />
        </G.Item>
      {{/if}}
    </FlexGrid>
    {{#if @tokens.length}}
      <HdsTable
        @caption={{t "components.scim.tokens.list.table.title"}}
        @valign="middle"
        data-test-scim-tokens-list-table
      >
        <:head as |H|>
          <H.Tr>
            <H.Th>
              {{t "components.scim.tokens.list.table.headers.name"}}
            </H.Th>
            <H.Th>
              {{t "components.scim.tokens.list.table.headers.date"}}
            </H.Th>
            <H.Th>
              {{t "components.scim.tokens.list.table.headers.synced"}}
            </H.Th>
            {{#if (can "delete scim-token")}}
              <H.Th data-test-scim-tokens-list-table-header-actions>
                {{t "components.scim.tokens.list.table.headers.actions"}}
              </H.Th>
            {{/if}}
          </H.Tr>
        </:head>
        <:body as |B|>
          {{#each @tokens key="id" as |token|}}
            <B.Tr data-test-scim-tokens-list-item>
              <B.Td data-test-scim-tokens-list-cell>
                {{token.displayName}}
              </B.Td>
              <B.Td data-test-scim-tokens-list-cell>
                <Time @date={{token.createdAt}} />
              </B.Td>
              <B.Td data-test-scim-tokens-list-cell>
                {{#if token.lastUsedAt}}
                  <Time @date={{token.lastUsedAt}} />
                {{else}}
                  {{t "components.scim.tokens.list.table.synced"}}
                {{/if}}
              </B.Td>
              {{#if (can "delete scim-token")}}
                <B.Td data-test-scim-tokens-list-cell>
                  <Flex @justifyContent="end">
                    <HdsDropdown data-test-more-menu as |dd|>
                      <dd.ToggleIcon
                        @hasChevron={{false}}
                        @icon="more-horizontal"
                        @text={{t "components.form.more"}}
                        data-test-more-menu-trigger
                      />
                      <dd.Interactive
                        @color="critical"
                        @icon="trash"
                        @text={{t "components.scim.tokens.list.table.action"}}
                        data-test-delete-scim-token-modal-trigger
                        {{on "click" (fn this.openDeleteTokenModal token)}}
                      />
                    </HdsDropdown>
                  </Flex>
                </B.Td>
              {{/if}}
            </B.Tr>
          {{/each}}
        </:body>
      </HdsTable>
      {{#if this.showDeleteTokenModal}}
        <Delete
          @onCancel={{this.closeDeleteTokenModal}}
          @onSubmit={{this.deleteToken}}
          @tokenToDelete={{this.tokenToDelete}}
          @organization={{@organization}}
        />
      {{/if}}
    {{/if}}
  </template>
}
